import { useEffect, useState } from 'react';
import { MapPin, Loader, X, Check, Crosshair } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LocationPicker } from './LocationPicker';
import { captureBestLocation } from '../lib/geolocation';
import type { GPSLocation } from '../lib/googleMaps';

interface LocationPickerModalProps {
  open: boolean;
  /** Existing device location to seed/adjust; if absent the pin seeds from GPS. */
  existing?: GPSLocation | null;
  /**
   * The property's saved location. When GPS is blocked/unavailable and there is
   * no existing device pin, the map still opens centered here so the tech can
   * drag the pin to the exact spot manually instead of hitting a dead end.
   */
  fallback?: GPSLocation | null;
  onSave: (loc: GPSLocation) => void;
  onClose: () => void;
}

/**
 * Map-based location capture: seeds a pin (from the device's existing location,
 * a GPS best-fix, or — when GPS is blocked — the property's saved location) and
 * lets the tech move the map under a fixed centre pin to the exact spot, then
 * Save. The confirmed coordinate is what gets stored — GPS is only the starting
 * guess, so a poor fix can't save the wrong place. Replaces the old single-shot
 * "Set GPS" capture across device forms.
 *
 * The map must never dead-end: a denied/blocked location permission (common on
 * a field Mac or an iPad where the tech tapped "Don't Allow") used to leave only
 * a re-failing "Use My Location" button with no way to place the pin at all.
 * Now, as long as a `fallback` (or `existing`) coordinate is available, the map
 * always renders and manual placement is possible.
 */
export function LocationPickerModal({ open, existing, fallback, onSave, onClose }: LocationPickerModalProps) {
  const { t } = useTranslation('inspection');
  const { t: tCommon } = useTranslation('common');

  const [seed, setSeed] = useState<GPSLocation | null>(null);
  const [seedAccuracy, setSeedAccuracy] = useState<number | null>(null);
  const [picked, setPicked] = useState<GPSLocation | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [failed, setFailed] = useState(false);
  // GPS was blocked/unavailable and we fell back to the property location — the
  // map is usable but the pin is a guess, so nudge the tech to place it exactly.
  const [gpsBlocked, setGpsBlocked] = useState(false);

  // `spinnerIfEmpty`: only cover the dialog with the "getting location" spinner
  // when there is nothing to show yet. If a fallback/existing pin is already on
  // the map (a manual retry, or a background refine), keep the map visible.
  const seedFromGps = async (spinnerIfEmpty: boolean) => {
    if (spinnerIfEmpty) setSeeding(true);
    setFailed(false);
    const fix = await captureBestLocation('picker');
    if (fix) {
      const loc = { latitude: fix.latitude, longitude: fix.longitude };
      setSeed(loc);
      setPicked(loc);
      setSeedAccuracy(fix.accuracy);
      setGpsBlocked(false);
    } else if (fallback) {
      // No usable fix, but we know where the property is — open the map there so
      // the tech can place the pin by hand rather than being stuck.
      setSeed((s) => s ?? fallback);
      setPicked((p) => p ?? fallback);
      setSeedAccuracy(null);
      setGpsBlocked(true);
    } else {
      setFailed(true);
    }
    setSeeding(false);
  };

  useEffect(() => {
    if (!open) return;
    setFailed(false);
    setGpsBlocked(false);
    if (existing) {
      setSeed(existing);
      setPicked(existing);
      setSeedAccuracy(null);
      setSeeding(false);
    } else if (fallback) {
      // Show the property location immediately so the map is never blank, then
      // refine to a live GPS fix in the background (without hiding the map).
      setSeed(fallback);
      setPicked(fallback);
      setSeedAccuracy(null);
      setSeeding(false);
      void seedFromGps(false);
    } else {
      setSeed(null);
      setPicked(null);
      setSeedAccuracy(null);
      void seedFromGps(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="bg-surface rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-line">
          <h3 className="text-lg font-semibold text-theme-primary flex items-center gap-2">
            <MapPin size={20} className="text-blue-600" /> {t('gps.confirmTitle')}
          </h3>
          <button onClick={onClose} aria-label={tCommon('actions.close')} className="p-1 rounded-full text-theme-secondary hover:text-theme-primary hover:bg-subtle">
            <X size={20} />
          </button>
        </div>

        <div className="p-4">
          {seeding && (
            <div className="h-[340px] flex flex-col items-center justify-center text-theme-secondary gap-2">
              <Loader size={24} className="animate-spin text-blue-500" />
              <span className="text-sm">{t('gps.gettingLocation')}</span>
            </div>
          )}

          {!seeding && seed && picked && (
            <>
              {gpsBlocked && (
                <div className="mb-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300">
                  {t('gps.blockedUsingProperty')}
                </div>
              )}
              <LocationPicker
                initial={seed}
                accuracyMeters={seedAccuracy}
                onChange={setPicked}
                height="340px"
                hint={t('gps.dragHint')}
              />
              <div className="mt-2 flex items-center justify-between gap-3 text-sm">
                <span className="font-mono text-theme-primary">
                  {picked.latitude.toFixed(6)}, {picked.longitude.toFixed(6)}
                </span>
                <button onClick={() => seedFromGps(false)} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
                  <Crosshair size={14} /> {t('gps.useMyLocation')}
                </button>
              </div>
            </>
          )}

          {!seeding && failed && !seed && (
            <div className="h-[220px] flex flex-col items-center justify-center text-center text-theme-secondary gap-3 px-4">
              <span className="text-sm">{t('gps.couldNotGetLocation')}</span>
              <button onClick={() => seedFromGps(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">
                <Crosshair size={16} /> {t('gps.useMyLocation')}
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-line">
          <button onClick={onClose} className="px-4 py-2 border border-line rounded-lg text-theme-primary hover:bg-subtle font-medium">
            {tCommon('actions.cancel')}
          </button>
          <button
            onClick={() => { if (picked) { onSave(picked); onClose(); } }}
            disabled={!picked}
            className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <Check size={18} /> {tCommon('actions.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
